import React, { useState } from "react";
import ApiKeyRevealModal from "./ApiKeyRevealModal";
import { getApiUrl } from "../../../../../../config/api";

const DICT = {
    createChannel: { en: "Create Channel", zh: "建立頻道" },
    channelName: { en: "Channel Name", zh: "頻道名稱" },
    channelNamePh: { en: "e.g. Partner Portal", zh: "例如：合作夥伴入口" },
    cancel: { en: "Cancel", zh: "取消" },
    create: { en: "Create", zh: "建立" },
    creating: { en: "Creating…", zh: "建立中…" },
    createError: { en: "Failed to create channel.", zh: "建立頻道失敗。" },
    allowedUrls: { en: "Allowed Origins (CORS)", zh: "允許的來源網址 (CORS)" },
    allowedUrlsDesc: {
        en: "Only these origins can call this channel's API. Leave empty to allow all.",
        zh: "只有這些來源可以呼叫此頻道的 API。留空表示允許所有來源。",
    },
    addUrl: { en: "Add URL", zh: "新增網址" },
    urlPh: { en: "https://example.com", zh: "https://example.com" },
    restrictAccess: { en: "Restrict by origin", zh: "限制來源網址" },
};

const t = (key, lang) => DICT[key]?.[lang] || DICT[key]?.en || key;

function CreateChannelModal({ language, currentUser, onClose, onCreated }) {
    const [channelName, setChannelName] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [revealKey, setRevealKey] = useState(null);
    const [createdName, setCreatedName] = useState("");

    // ── allowed URL state ──────────────────────────────────────────────────
    const [restrictOrigin, setRestrictOrigin] = useState(false);
    const [allowedUrls, setAllowedUrls] = useState([]);
    const [urlInput, setUrlInput] = useState("");
    const [urlError, setUrlError] = useState("");

    const isValidUrl = (v) => {
        try {
            new URL(v);
            return true;
        } catch {
            return false;
        }
    };

    const addUrl = () => {
        const val = urlInput.trim().replace(/\/+$/, ""); // strip trailing slash
        if (!val) return;
        if (!isValidUrl(val)) {
            setUrlError(
                language === "zh"
                    ? "請輸入有效的網址"
                    : "Please enter a valid URL",
            );
            return;
        }
        if (allowedUrls.includes(val)) {
            setUrlError(
                language === "zh" ? "此網址已存在" : "URL already added",
            );
            return;
        }
        setAllowedUrls((prev) => [...prev, val]);
        setUrlInput("");
        setUrlError("");
    };

    const removeUrl = (url) =>
        setAllowedUrls((prev) => prev.filter((u) => u !== url));

    const handleCreate = async () => {
        const trimmed = channelName.trim();
        if (!trimmed) return;
        setSubmitting(true);
        setError(null);
        try {
            const token = await currentUser.getIdToken();
            const body = { channel_name: trimmed };
            if (restrictOrigin) body.allowed_url = allowedUrls;
            const res = await fetch(`${getApiUrl()}/api/v2/channels`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(body),
            });
            const data = await res.json();
            if (data.success) {
                onCreated();
                setCreatedName(trimmed);
                setRevealKey(data.data.api_key);
            } else {
                setError(data.message || t("createError", language));
            }
        } catch (err) {
            setError(err.message || t("createError", language));
        } finally {
            setSubmitting(false);
        }
    };

    // Show reveal modal once key is available
    if (revealKey) {
        return (
            <ApiKeyRevealModal
                language={language}
                apiKey={revealKey}
                channelName={createdName}
                onClose={onClose}
            />
        );
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div
                className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                onClick={() => !submitting && onClose()}
            />
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
                {/* Header */}
                <div className="flex items-start justify-between px-6 pt-6 pb-4 border-b border-gray-100">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center">
                            <svg
                                className="w-4 h-4 text-orange-600"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
                                />
                            </svg>
                        </div>
                        <div>
                            <h3 className="text-lg font-bold text-gray-900">
                                {t("createChannel", language)}
                            </h3>
                            <p className="text-sm text-gray-500">
                                {language === "zh"
                                    ? "設定新的 API 頻道"
                                    : "Set up a new API channel"}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        disabled={submitting}
                        className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg p-1.5 transition-all disabled:opacity-50"
                    >
                        <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                            />
                        </svg>
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 space-y-5">
                    {/* Channel name */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            {t("channelName", language)}
                        </label>
                        <input
                            type="text"
                            value={channelName}
                            onChange={(e) => setChannelName(e.target.value)}
                            onKeyDown={(e) =>
                                e.key === "Enter" && handleCreate()
                            }
                            placeholder={t("channelNamePh", language)}
                            className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 transition-all"
                            autoFocus
                        />
                    </div>

                    {/* Restrict origin toggle */}
                    <div className="border border-gray-200 rounded-xl overflow-hidden">
                        <button
                            type="button"
                            onClick={() => setRestrictOrigin((v) => !v)}
                            className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
                        >
                            <div className="flex items-center gap-2.5">
                                <svg
                                    className="w-4 h-4 text-gray-500"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                    />
                                </svg>
                                <span className="text-sm font-medium text-gray-700">
                                    {t("restrictAccess", language)}
                                </span>
                            </div>
                            <div
                                className={`w-9 h-5 rounded-full transition-colors relative ${restrictOrigin ? "bg-orange-500" : "bg-gray-300"}`}
                            >
                                <div
                                    className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${restrictOrigin ? "translate-x-4" : "translate-x-0.5"}`}
                                />
                            </div>
                        </button>

                        {restrictOrigin && (
                            <div className="px-4 pb-4 pt-3 space-y-3">
                                <p className="text-xs text-gray-500">
                                    {t("allowedUrlsDesc", language)}
                                </p>

                                {/* URL input row */}
                                <div className="flex gap-2">
                                    <input
                                        type="url"
                                        value={urlInput}
                                        onChange={(e) => {
                                            setUrlInput(e.target.value);
                                            setUrlError("");
                                        }}
                                        onKeyDown={(e) =>
                                            e.key === "Enter" &&
                                            (e.preventDefault(), addUrl())
                                        }
                                        placeholder={t("urlPh", language)}
                                        className="flex-1 px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 transition-all"
                                    />
                                    <button
                                        type="button"
                                        onClick={addUrl}
                                        className="px-3 py-2 text-sm font-medium text-white bg-orange-500 hover:bg-orange-600 rounded-lg transition-colors"
                                    >
                                        {t("addUrl", language)}
                                    </button>
                                </div>
                                {urlError && (
                                    <p className="text-xs text-red-500">
                                        {urlError}
                                    </p>
                                )}

                                {/* URL tags */}
                                {allowedUrls.length > 0 && (
                                    <div className="flex flex-wrap gap-2">
                                        {allowedUrls.map((url) => (
                                            <span
                                                key={url}
                                                className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-orange-50 border border-orange-200 text-orange-700 text-xs rounded-lg font-medium"
                                            >
                                                {url}
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        removeUrl(url)
                                                    }
                                                    className="hover:text-red-500 transition-colors"
                                                >
                                                    <svg
                                                        className="w-3 h-3"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2.5}
                                                            d="M6 18L18 6M6 6l12 12"
                                                        />
                                                    </svg>
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                )}

                                {allowedUrls.length === 0 && (
                                    <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                                        {language === "zh"
                                            ? "⚠ 已開啟限制但尚未新增任何網址，目前所有來源仍可存取。"
                                            : "⚠ Restriction enabled but no URLs added yet — all origins are still allowed."}
                                    </p>
                                )}
                            </div>
                        )}
                    </div>

                    {error && <p className="text-sm text-red-500">{error}</p>}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50/50">
                    <button
                        onClick={onClose}
                        disabled={submitting}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-all disabled:opacity-50"
                    >
                        {t("cancel", language)}
                    </button>
                    <button
                        onClick={handleCreate}
                        disabled={submitting || !channelName.trim()}
                        className="px-5 py-2 text-sm font-medium text-white bg-orange-500 hover:bg-orange-600 rounded-xl shadow-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {submitting ? (
                            <div className="flex items-center gap-2">
                                <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin"></div>
                                {t("creating", language)}
                            </div>
                        ) : (
                            t("create", language)
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default CreateChannelModal;
